/**
 * Pure currency math shared across the multi-currency pricing UI.
 *
 * The model: **USD is the functional base**. Every currency is a *view* over a
 * USD amount — `amount(currency) = usdAmount × displayFactor(currency)`. The
 * "local" (ILS) currency is the accounting/reporting local; the "primary" (USD)
 * currency is the reporting base the cross-side summary is expressed in.
 *
 * These helpers were duplicated inline across AccountingTab, the currency
 * selector, RateControlBox and the Change Rate dialog. Centralising them keeps
 * the (subtle) rules in one place and lets them be unit-tested directly.
 */

/** Loose id equality — currencies flow through as either `_id` strings or objects. */
export const sameCurrency = (a: unknown, b: unknown): boolean =>
    !!a && !!b && String(a) === String(b);

/** Round a monetary amount to 2 decimals. */
export const roundAmount = (n: number): number => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Round an exchange rate. Default 4 decimals — the precision shown and stored
 * in the Change Rate dialog and the Accounting tab.
 */
export const roundRate = (n: number, decimals = 4): number => {
    const factor = 10 ** decimals;
    return Math.round((Number(n) || 0) * factor) / factor;
};

/**
 * Is a stored rate an explicit user override, or a default/backfill?
 *
 * A foreign-currency rate of exactly `1` is treated as "not really set" (a
 * legacy/backfill default), so callers fall back to the market rate; a rate
 * like `2.6` is honoured.
 *
 * KNOWN LIMITATION: this misfires for a *legitimate* parity rate of exactly
 * `1.0` (two currencies genuinely at par) — that value is indistinguishable
 * from the backfill default. The robust fix is a nullable rate / explicit
 * "isRateSet" flag on the data model; tracked as follow-up.
 */
export const isRateExplicit = (stored: unknown): boolean => {
    const s = Number(stored) || 0;
    return s > 0 && s !== 1;
};

/** `isRateExplicit` with a tolerance — for rates DERIVED by division rather
 *  than stored, where a backfilled 1 comes back as 0.9999999999. */
const isDerivedRateExplicit = (n: unknown): boolean => {
    const v = Number(n) || 0;
    return v > 0 && Math.abs(v - 1) > 1e-6;
};

/**
 * Resolve a DOCUMENT's local rate: local (ILS) units per **1 unit of the
 * document's own currency**.
 *
 * Two rules, and a consumer needs BOTH. Having one without the other is what
 * printed `Rate: 1.00000 ₪ per USD` on a USD voucher tooltip, and `789.33₪`
 * as the local value of a ₪255.34 remittance:
 *
 *   1. **A local-currency document has nothing to convert — its rate is 1**,
 *      whatever it inherited. Covering documents (RMT / Receipt / PRQ) take
 *      their rate from the document they cover, so an ILS remittance settling
 *      a USD PRQ arrives carrying the PRQ's ₪-per-$ rate. Multiplying the
 *      shekel amount by that inflates it by the rate.
 *   2. **A foreign document storing exactly 1 is not "worth one shekel"** — it
 *      is a backfill written when no market rate had loaded at issue time
 *      (see `isRateExplicit`). Fall through rather than trust it. The same
 *      applies to an effective rate of 1, because `amountLocal` is usually
 *      `amount × storedLocalRate` and so reproduces the bad 1.
 *
 * Priority: local doc → `1` · explicit stored rate · effective
 * `amountLocal / amount` · caller's fallback · else `undefined`, so a caller
 * can omit the rate line instead of printing a lie.
 */
export const resolveDocumentLocalRate = (params: {
    /** The document's own currency (`info.currency`), id or populated object. */
    currencyId: unknown;
    localId: unknown;
    /** `info.localRate` as stored. */
    storedLocalRate?: unknown;
    /** `info.amount` / `info.amountLocal`, for legacy rows with no localRate. */
    amount?: unknown;
    amountLocal?: unknown;
    /** Last resort, e.g. the transaction-date market rate. */
    fallbackRate?: unknown;
}): number | undefined => {
    const { currencyId, localId, storedLocalRate, amount, amountLocal, fallbackRate } = params;

    if (sameCurrency(currencyId, localId)) return 1;
    if (isRateExplicit(storedLocalRate)) return Number(storedLocalRate);

    // Magnitudes, not signed values: a credit voucher / refund is negative on
    // both sides, and requiring both to be positive left every negative
    // document with no effective rate to recover — silently falling through to
    // today's market rate instead of the one it was issued at.
    const amt = Math.abs(Number(amount) || 0);
    const amtLocal = Math.abs(Number(amountLocal) || 0);
    const effective = amt > 0 && amtLocal > 0 ? amtLocal / amt : 0;
    if (isDerivedRateExplicit(effective)) return effective;

    return isDerivedRateExplicit(fallbackRate) ? Number(fallbackRate) : undefined;
};

/**
 * Resolve the PRIMARY→LOCAL rate (local units per **1 primary unit**) to use
 * when rendering a document's "primary / reporting amount".
 *
 * A document persists exactly ONE rate, and what that number MEANS depends on
 * the document's own currency (see `ChangeRate`/`TableActionButtons`, which
 * write it, and `RateControlBox`, which displays it):
 *
 *   1. **Local-currency doc** → the stored rate IS the local↔primary (₪↔$)
 *      rate. `TableActionButtons` deliberately stores the PRIMARY row's rate
 *      rather than 1, because a doc→local rate of 1 carries no information.
 *   2. **Primary-currency doc** → the stored doc→local rate IS, by definition,
 *      the primary→local rate.
 *   3. **Any other foreign doc** (EUR with a USD primary) → the stored rate is
 *      that currency→local. Its primary→local rate is `documentinfo.primaryRate`
 *      when the document was written after that column existed; an imported
 *      document has none but carries Galor's exact local AND primary amounts,
 *      whose ratio is that rate. Only with neither is the market rate used.
 *
 * Ignoring cases 1 and 2 and always reaching for the market rate is what made a
 * remittance's "Primary Amount" column disagree with the Change Rate dialog the
 * moment a user overrode the rate — and only then, since before an override the
 * stored rate and the market rate are the same number.
 *
 * Returns `0` when no rate is resolvable, so a caller can omit the column
 * instead of printing a lie.
 */
export const resolveDocumentPrimaryToLocalRate = (params: {
    /** The document's own currency (`info.currency`), id or populated object. */
    currencyId: unknown;
    primaryId: unknown;
    localId: unknown;
    /** `info.rate` / `info.localRate` as stored, or the form's `rate` field. */
    storedRate?: unknown;
    /** Live primary→local market rate — the fallback for case 3. */
    marketPrimaryToLocal?: unknown;
    /** Case 3 only: `info.primaryRate`, and the stored amounts it can be
     *  recovered from on an imported document. Omitted by callers that only
     *  ever handle local / primary documents. */
    storedPrimaryRate?: unknown;
    amount?: unknown;
    amountLocal?: unknown;
    amountObj?: unknown;
}): number => {
    const { currencyId, primaryId, localId, storedRate, marketPrimaryToLocal } = params;

    // A client whose primary IS its local currency has nothing to convert.
    if (sameCurrency(primaryId, localId)) return 1;

    const docCarriesPrimaryRate =
        sameCurrency(currencyId, localId) || sameCurrency(currencyId, primaryId);
    if (docCarriesPrimaryRate && isRateExplicit(storedRate)) return Number(storedRate);

    if (!docCarriesPrimaryRate) {
        if (isRateExplicit(params.storedPrimaryRate)) return Number(params.storedPrimaryRate);
        // amountObj === amount is the old "no rate was ever applied" fallback
        // (a €400 document stored as $400), not a dollar figure — its ratio
        // would be the document's own →local rate, not ₪ per $.
        const amt = Math.abs(Number(params.amount) || 0);
        const local = Math.abs(Number(params.amountLocal) || 0);
        const obj = Math.abs(Number(params.amountObj) || 0);
        if (local > 0 && obj > 0 && Math.abs(obj - amt) >= 0.005) {
            const derived = local / obj;
            if (isDerivedRateExplicit(derived)) return derived;
        }
    }

    return Number(marketPrimaryToLocal) || 0;
};

/**
 * Resolve the rate a DERIVED document form (PRQ / Remittance / Receipt / Agent
 * Invoice) should seed into `RateControlBox` — in the semantics that box
 * actually displays, which is not the same question for every document.
 *
 * The box relabels itself: for a foreign source it shows local-per-document-
 * unit ("Rate ₪ → $" on a USD voucher), but for a LOCAL-currency source, where
 * document→local is a tautological 1, it switches to the local↔PRIMARY rate.
 * `resolveDocumentLocalRate` answers the first question only, and every form
 * called it (or an inline copy) for both — which is what showed
 * "Rate ₪ → $ = 1" on a ₪152 voucher whose ₪-per-$ rate of 2.9798 was sitting
 * in `info.rate` all along.
 *
 * Where a LOCAL document's ₪→$ rate lives, in priority order:
 *   1. `info.rate` — document-currency units per **1 primary unit**, so on an
 *      ILS document it IS ₪ per $. Written by `deriveServiceDocumentRates` and
 *      by the Galor import (`trans_net / trans_net_obj`).
 *   2. `amount / amountObj` — the same figure recovered from stored amounts.
 *   3. `marketPrimaryToLocal` — last resort, and the only source for the ~11%
 *      of local rows carrying neither.
 *
 * Returns `undefined` when nothing resolves, so a caller can leave the field to
 * its own market lookup instead of seeding a fabricated 1 — which downstream
 * turns into `amountObj = amount / 1`, reporting a ₪152 payment as $152.
 */
export const resolveDocumentFormRate = (params: {
    /** The document's own currency (`info.currency`), id or populated object. */
    currencyId: unknown;
    localId: unknown;
    primaryId: unknown;
    /** `info.localRate` — local units per 1 document unit. */
    storedLocalRate?: unknown;
    /** `info.rate` — document units per 1 primary unit. */
    storedRate?: unknown;
    amount?: unknown;
    amountLocal?: unknown;
    amountObj?: unknown;
    /** Live primary→local (USD→ILS) market rate — fallback for a local doc. */
    marketPrimaryToLocal?: unknown;
    /** Live document→local market rate — fallback for a foreign doc. */
    fallbackRate?: unknown;
}): number | undefined => {
    const {
        currencyId,
        localId,
        primaryId,
        storedLocalRate,
        storedRate,
        amount,
        amountLocal,
        amountObj,
        marketPrimaryToLocal,
        fallbackRate
    } = params;

    // Foreign document: unchanged behaviour — document→local.
    if (!sameCurrency(currencyId, localId)) {
        return resolveDocumentLocalRate({
            currencyId,
            localId,
            storedLocalRate,
            amount,
            amountLocal,
            fallbackRate
        });
    }

    // A client whose primary IS its local currency has nothing to convert.
    if (sameCurrency(primaryId, localId)) return 1;

    if (isRateExplicit(storedRate)) return Number(storedRate);

    // Magnitudes — a credit note is negative on both sides (see
    // `resolveDocumentLocalRate`).
    const amt = Math.abs(Number(amount) || 0);
    const amtObj = Math.abs(Number(amountObj) || 0);
    const effective = amt > 0 && amtObj > 0 ? amt / amtObj : 0;
    if (isDerivedRateExplicit(effective)) return effective;

    return isDerivedRateExplicit(marketPrimaryToLocal) ? Number(marketPrimaryToLocal) : undefined;
};

/** Unwrap an exchange-rates lookup result to its sell rate. */
const sellRate = (r: unknown): number =>
    Number((r as { exchangeRateSell?: number } | null)?.exchangeRateSell) || 0;

/**
 * `resolveDocumentFormRate` applied to a source document's `info`, with the two
 * market fallbacks wired to an exchange-rates `getConvertRate`.
 *
 * The four covering-document forms each carried their own copy of this
 * resolution, and each copy had to be fixed separately every time the rules
 * moved. They now share this one.
 */
export const resolveSourceDocumentRateForForm = (
    info: unknown,
    opts: {
        localId: unknown;
        primaryId: unknown;
        // Loosely typed on purpose: this stays decoupled from the exchange-rates
        // context's own `GetConvertRate`, whose optional/nullable parameters a
        // stricter `unknown` signature would reject.
        getConvertRate: (from?: any, to?: any) => any;
    }
): number | undefined => {
    const i = (info || {}) as Record<string, any>;
    const { localId, primaryId, getConvertRate } = opts;
    const currencyId = typeof i.currency === 'object' ? i.currency?._id : i.currency;
    const isLocalDoc = sameCurrency(currencyId, localId);

    return resolveDocumentFormRate({
        currencyId,
        localId,
        primaryId,
        storedLocalRate: i.localRate,
        storedRate: i.rate,
        amount: i.amount,
        amountLocal: i.amountLocal,
        amountObj: i.amountObj,
        // Only the branch that can actually be reached is looked up.
        marketPrimaryToLocal:
            isLocalDoc && primaryId && !sameCurrency(primaryId, localId)
                ? sellRate(getConvertRate(primaryId, localId))
                : 0,
        fallbackRate: !isLocalDoc && currencyId ? sellRate(getConvertRate(currencyId, localId)) : 0
    });
};

/**
 * A document's amount expressed in an arbitrary TARGET currency.
 *
 * Priority, and the order matters more than any of the individual steps:
 *   1. **The document is already in the target currency** → its own face value,
 *      untouched. No rate may be applied to it.
 *   2. **A figure the document itself stores** — `amountLocal` for the local
 *      side, `amountObj` for the primary side.
 *   3. **The document's OWN rate** (`localRate` / `rate`).
 *   4. **A market rate**, and only then.
 *
 * A market rate is a statement about today; a document is a statement about the
 * day it was issued. Re-pricing one at the other's rate silently rewrites
 * history — which is what rendered a ₪1,524.00 receipt as ₪1,529.79 in the
 * travel file's Accounting grid: the grid took the receipt's stored $500.13 and
 * multiplied it by today's 3.0588 instead of showing the shekels the customer
 * was actually charged at 3.0472 on the day.
 *
 * Sign is preserved. Pass the RAW stored amount and apply any display sign
 * (refund / repay) outside. Returns `undefined` when nothing resolves, so a
 * caller can render blank instead of a fabricated figure.
 */
export const resolveDocumentAmountInCurrency = (params: {
    /** The document's own currency (`info.currency`), id or populated object. */
    currencyId: unknown;
    /** The currency to express the amount in. */
    targetId: unknown;
    localId: unknown;
    primaryId: unknown;
    /** `info.amount` — the document's face value, in its own currency. */
    amount?: unknown;
    amountLocal?: unknown;
    amountObj?: unknown;
    /** `info.rate` — document units per 1 primary unit. */
    storedRate?: unknown;
    /** `info.localRate` — local units per 1 document unit. */
    storedLocalRate?: unknown;
    /** Market document→local, the fallback inside the rate resolvers. */
    marketDocToLocal?: unknown;
    /** Market primary→local (USD→ILS). */
    marketPrimaryToLocal?: unknown;
    /** Market document→target, the last resort for a target that is neither
     *  the local nor the primary currency. */
    marketDocToTarget?: unknown;
}): number | undefined => {
    const {
        currencyId,
        targetId,
        localId,
        primaryId,
        amount,
        amountLocal,
        amountObj,
        storedRate,
        storedLocalRate,
        marketDocToLocal,
        marketPrimaryToLocal,
        marketDocToTarget
    } = params;

    const amt = Number(amount) || 0;
    // Zero is zero in every currency — and this is also what stops a stale
    // `amountObj` from resurrecting a document whose amount was later zeroed.
    if (!amt) return 0;

    // Already in the currency asked for: nothing to convert, and nothing that
    // may be applied. This single line is the fix for the receipt above.
    if (sameCurrency(currencyId, targetId)) return amt;

    if (sameCurrency(targetId, localId)) {
        // `amountLocal === amount` on a non-local document encodes "no rate was
        // ever set", not a rate of 1 — see `resolveDocumentLocalRate`.
        const stored = Number(amountLocal) || 0;
        if (stored && Math.abs(stored - amt) >= 0.005) return stored;
        const docToLocal = resolveDocumentLocalRate({
            currencyId,
            localId,
            storedLocalRate,
            amount: amt,
            amountLocal: stored,
            fallbackRate: marketDocToLocal
        });
        return docToLocal ? amt * docToLocal : undefined;
    }

    if (sameCurrency(targetId, primaryId)) {
        // `amountObj === amount` on a non-primary document encodes "no rate was
        // ever applied" (a €100 doc stored as $100), not a dollar figure — same
        // sentinel the local branch guards against. Recompute when it matches.
        const stored = Number(amountObj) || 0;
        if (stored && Math.abs(stored - amt) >= 0.005) return stored;
        // Nothing stored, so derive it. Local is the only leg either rate field
        // describes, so the conversion goes through it: doc → local → primary.
        const docToLocal = resolveDocumentLocalRate({
            currencyId,
            localId,
            storedLocalRate,
            amount: amt,
            amountLocal,
            fallbackRate: marketDocToLocal
        });
        const primaryToLocal = resolveDocumentPrimaryToLocalRate({
            currencyId,
            primaryId,
            localId,
            storedRate,
            marketPrimaryToLocal
        });
        return docToLocal && primaryToLocal ? (amt * docToLocal) / primaryToLocal : undefined;
    }

    const market = Number(marketDocToTarget) || 0;
    return market ? amt * market : undefined;
};

/**
 * `resolveDocumentAmountInCurrency` applied to a document's `info`, with the
 * three market fallbacks wired to an exchange-rates `getConvertRate` — the
 * companion to `resolveSourceDocumentRateForForm`.
 */
export const resolveDocumentAmountForTable = (
    info: unknown,
    opts: {
        targetId: unknown;
        localId: unknown;
        primaryId: unknown;
        // Loosely typed for the same reason as `resolveSourceDocumentRateForForm`.
        // Optional: the server-side totals have no rate lookup to hand, and the
        // market is only ever the last resort anyway — every document in the
        // local or the primary currency resolves from its own stored figures.
        getConvertRate?: (from?: any, to?: any) => any;
    }
): number | undefined => {
    const i = (info || {}) as Record<string, any>;
    const { targetId, localId, primaryId, getConvertRate } = opts;
    const currencyId = typeof i.currency === 'object' ? i.currency?._id : i.currency;

    // Rates are stored foreign→ILS only, so a doc→target row often does not
    // exist (e.g. ILS→USD); invert the reverse lookup when it doesn't.
    const marketRate = (fromId: unknown, toId: unknown): number => {
        if (!fromId || !toId) return 0;
        if (sameCurrency(fromId, toId)) return 1;
        if (!getConvertRate) return 0;
        const direct = sellRate(getConvertRate(fromId, toId));
        if (direct) return direct;
        const inverse = sellRate(getConvertRate(toId, fromId));
        return inverse ? 1 / inverse : 0;
    };

    return resolveDocumentAmountInCurrency({
        currencyId,
        targetId,
        localId,
        primaryId,
        amount: i.amount,
        amountLocal: i.amountLocal,
        amountObj: i.amountObj,
        storedRate: i.rate,
        storedLocalRate: i.localRate,
        marketDocToLocal: marketRate(currencyId, localId),
        marketPrimaryToLocal: sameCurrency(primaryId, localId)
            ? 1
            : marketRate(primaryId, localId),
        marketDocToTarget: marketRate(currencyId, targetId)
    });
};

/**
 * `displayFactor(c)` = amount in currency `c` per **1 USD** (the USD-base view).
 *   - primary (USD):        1
 *   - local (ILS):          the USD↔ILS reference rate (`localRefRate`)
 *   - any other foreign c:  primaryToLocal ÷ marketToLocal(c)
 *
 * `primaryToLocal` is the USD→ILS market rate; `marketToLocal(c)` is the
 * c→ILS market rate; `localRefRate` is the USD↔ILS rate the caller wants to
 * use for the local side (its stored rate if explicit, else market).
 */
export const displayFactor = (params: {
    currencyId: unknown;
    primaryId: unknown;
    localId: unknown;
    /** USD → ILS market rate. */
    primaryToLocal: number;
    /** USD↔ILS reference rate used for the local (ILS) side. */
    localRefRate: number;
    /** currency → ILS market rate. */
    marketToLocal: (currencyId: unknown) => number;
}): number => {
    const { currencyId, primaryId, localId, primaryToLocal, localRefRate, marketToLocal } = params;
    if (sameCurrency(currencyId, primaryId)) return 1;
    if (!currencyId || sameCurrency(currencyId, localId)) return localRefRate || 1;
    const m = marketToLocal(currencyId);
    return m ? (primaryToLocal || 1) / m : 1;
};

/**
 * Factor to express a supplier amount in the SELLING currency so that
 * markup = Selling − Supplier is computed in one currency (the USD base):
 *   supplierToSellingRate = displayFactor(selling) ÷ displayFactor(supplier)
 * Returns 1 when the supplier factor is 0 (degenerate).
 */
export const supplierToSellingFactor = (sellingDF: number, supplierDF: number): number =>
    supplierDF ? sellingDF / supplierDF : 1;

/**
 * Markup in the selling currency: Selling − Supplier×k (k = supplierToSelling
 * factor). `percent` is markup as a % of the supplier-in-selling amount.
 * At k=1 with Selling === Supplier this is a clean 0 / 0%.
 */
export const computeMarkup = (
    selling: number,
    supplier: number,
    k = 1
): { amount: number; percent: number } => {
    const supplierInSelling = (Number(supplier) || 0) * (Number(k) || 1);
    const amount = roundAmount((Number(selling) || 0) - supplierInSelling);
    const percent = supplierInSelling ? Math.round((amount / supplierInSelling) * 10000) / 100 : 0;
    return { amount, percent };
};

/**
 * Express `amount` (denominated in `currencyId`, optionally at an explicit
 * stored `storedRate`) in the PRIMARY (USD) reporting currency. Mirrors
 * AccountingTab's per-side → USD conversion so a grid/USD column always matches
 * the tab's Revenue/Cost figures.
 *
 * Rate semantics follow `computeSideRate`:
 *   - foreign side: `storedRate` = local (ILS) units per 1 unit of currencyId.
 *   - local  side: `storedRate` = the ILS↔USD reference (local per 1 USD).
 * A stored rate is honoured only when explicit (see `isRateExplicit`); a
 * backfill `1` falls back to the market rate. This is what lets the Change Rate
 * dialog — which writes an explicit rate onto the service — drive the USD value.
 *
 * Returns the USD amount plus `perPrimary` = units of `currencyId` per 1 USD
 * (the "¥ per $" figure a tooltip can show).
 */
export const amountToPrimary = (params: {
    amount: number;
    currencyId: unknown;
    storedRate: unknown;
    primaryId: unknown;
    localId: unknown;
    /** USD → ILS market rate. */
    primaryToLocal: number;
    /** currency → ILS market rate (live or transaction-date historical). */
    marketToLocal: (currencyId: unknown) => number;
}): { usd: number; perPrimary: number } => {
    const { amount, currencyId, storedRate, primaryId, localId, primaryToLocal, marketToLocal } =
        params;
    const amt = Number(amount) || 0;
    // Already in USD.
    if (sameCurrency(currencyId, primaryId)) return { usd: amt, perPrimary: 1 };
    // Local (ILS) side, or unknown currency treated as local: rate is ILS per USD.
    if (!currencyId || sameCurrency(currencyId, localId)) {
        const ilsPerUsd = isRateExplicit(storedRate) ? Number(storedRate) : primaryToLocal;
        return { usd: ilsPerUsd ? amt / ilsPerUsd : 0, perPrimary: ilsPerUsd || 1 };
    }
    // Any other foreign currency: rate is ILS per 1 unit of currencyId.
    const toLocal = isRateExplicit(storedRate) ? Number(storedRate) : marketToLocal(currencyId);
    if (!toLocal || !primaryToLocal) return { usd: amt, perPrimary: 1 };
    return { usd: (amt * toLocal) / primaryToLocal, perPrimary: primaryToLocal / toLocal };
};

export type RateRow = {
    currency: string;
    amount: number;
    rate: number;
    isLocalCurrency?: boolean;
    [key: string]: unknown;
};

/**
 * Change Rate dialog recompute. Applies an `amount` or `rate` edit on
 * `editedCurrency` and re-derives every other row, keeping the LOCAL row's
 * amount as the ILS pivot.
 *
 * `anchorCurrency` is the radio-selected row whose AMOUNT stays fixed on a
 * *rate* change (TB "anchor" behaviour):
 *   - anchor = local (ILS): ILS is held → each foreign amount = ILS ÷ its rate.
 *   - anchor = a foreign row: its amount+rate are held → ILS recomputes from it,
 *     and the other foreign amounts re-derive from the new ILS.
 *
 * Pure: returns a new array (or the input unchanged when the edit is invalid).
 * Amounts are rounded to 2 decimals; foreign rates to 4; the local rate is 1.
 */
export const recomputeRateRows = (
    rows: RateRow[],
    editedCurrency: string,
    field: 'amount' | 'rate',
    rawValue: number,
    anchorCurrency: string | null
): RateRow[] => {
    const newValue = Number(rawValue);
    if (isNaN(newValue)) return rows;
    if (field === 'rate' && newValue <= 0) return rows;

    const updated = rows.map((r) => ({ ...r }));
    const localRow = updated.find((r) => r.isLocalCurrency);
    const editedRow = updated.find((r) => r.currency === editedCurrency);
    if (!localRow || !editedRow) return rows;

    const isAnchor = editedCurrency === anchorCurrency;

    if (field === 'amount') {
        editedRow.amount = newValue;
        if (editedRow.isLocalCurrency) {
            updated.forEach((r) => {
                if (!r.isLocalCurrency && Number(r.amount) > 0) r.rate = newValue / Number(r.amount);
            });
        } else if (isAnchor) {
            localRow.amount = newValue * Number(editedRow.rate);
            updated.forEach((r) => {
                if (!r.isLocalCurrency && r.currency !== editedCurrency && Number(r.amount) > 0)
                    r.rate = Number(localRow.amount) / Number(r.amount);
            });
        } else if (Number(localRow.amount) > 0 && newValue > 0) {
            editedRow.rate = Number(localRow.amount) / newValue;
        }
    } else {
        // Rate change: keep the ANCHOR's amount fixed, re-derive the rest.
        if (!editedRow.isLocalCurrency) {
            editedRow.rate = newValue;
            const anchorRow = updated.find((r) => r.currency === anchorCurrency) || editedRow;
            if (anchorRow.isLocalCurrency) {
                // Anchor = ILS: ILS fixed → every foreign amount = ILS / its rate.
                updated.forEach((r) => {
                    if (!r.isLocalCurrency && Number(r.rate) > 0)
                        r.amount = Number(localRow.amount) / Number(r.rate);
                });
            } else {
                // Anchor = foreign: its amount+rate stay → ILS recomputes,
                // other foreign amounts re-derive from ILS.
                localRow.amount = Number(anchorRow.amount) * Number(anchorRow.rate);
                updated.forEach((r) => {
                    if (!r.isLocalCurrency && r.currency !== anchorRow.currency && Number(r.rate) > 0)
                        r.amount = Number(localRow.amount) / Number(r.rate);
                });
            }
        }
    }

    return updated.map((r) => ({
        ...r,
        amount: roundAmount(Number(r.amount)),
        rate: r.isLocalCurrency ? 1 : roundRate(Number(r.rate))
    }));
};
